import React from "react";
import { Helmet } from "react-helmet-async";
import { ChevronDown, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";

const FaqEmploi = ({ setOpenIndex, openIndex, toggleFAQ, idCategorie }) => {
  const faqList = [
    {
      question: () => (
        <strong>1. Comment poster une offre d'emploi sur Deal.mg ?</strong>
      ),
      answer: () => (
        <p>
          Pour publier une <strong>annonce de travail</strong>, cliquez sur le
          bouton « Publier une annonce », choisissez la catégorie « RH / Emploi
          », puis remplissez les détails de votre offre. Soyez le plus précis
          possible sur le poste, les compétences requises et le lieu de travail
          à Madagascar pour attirer les meilleurs candidats.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          2. Comment vérifier la fiabilité d'une annonce de travail ?
        </strong>
      ),
      answer: () => (
        <p>
          Deal.mg lutte activement contre les fraudes. Nous vérifions les
          annonces et les profils des recruteurs. Toutefois, soyez vigilants :
          une offre sérieuse ne demande jamais d'argent pour postuler ou pour
          obtenir un équipement de travail. Si une annonce vous semble suspecte,
          utilisez le bouton « Signaler ».
        </p>
      ),
    },
    {
      question: () => (
        <strong>3. Comment postuler à une offre d'emploi en ligne ?</strong>
      ),
      answer: () => (
        <p>
          Cliquez sur l'annonce qui vous intéresse et utilisez le bouton de
          contact pour envoyer votre CV directement au recruteur. Pensez à
          utiliser un titre clair pour votre email ou message afin de faciliter
          le traitement de votre candidature.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          4. Puis-je trouver des offres d'emploi urgentes sur la plateforme ?
        </strong>
      ),
      answer: () => (
        <p>
          Oui, de nombreux recruteurs cherchent à pourvoir des postes
          rapidement. Utilisez la barre de recherche avec des termes comme «
          <strong>emploi urgent</strong> » ou filtrez par date de publication
          pour trouver les opportunités les plus récentes.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          5. Quels types de contrats sont disponibles sur Deal.mg ?
        </strong>
      ),
      answer: () => (
        <p>
          Vous trouverez tous types de contrats :{" "}
          <strong>CDI, CDD, stages,</strong> ainsi que des missions en
          freelance. Pensez à préciser votre type de contrat recherché dans vos
          filtres de recherche.
        </p>
      ),
    },
    {
      question: () => (
        <strong>6. Comment rédiger un CV efficace pour Deal.mg ?</strong>
      ),
      answer: () => (
        <p>
          Mettez en avant vos compétences techniques et vos expériences passées.
          Inutile de faire un CV de plusieurs pages ; soyez concis et adaptez
          votre profil à l'annonce. N'oubliez pas d'indiquer votre localisation
          exacte pour faciliter la mise en relation avec les employeurs locaux.
        </p>
      ),
    },
    {
      question: () => (
        <strong>7. Les offres d'emploi sont-elles géolocalisées ?</strong>
      ),
      answer: () => (
        <p>
          Oui. Pour faciliter la <strong>recherche d'emploi</strong>, nos
          annonces sont classées par ville (Antananarivo, Toamasina, Antsirabe,
          etc.). Cela permet aux candidats de trouver un poste à proximité de
          leur domicile.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          8. Une offre d'emploi peut-elle me demander de payer des frais ?
        </strong>
      ),
      answer: () => (
        <p>
          <strong>Non</strong>. Une offre sérieuse ne demande jamais d'argent
          pour postuler ou pour obtenir un équipement de travail. Signalez
          immédiatement toute demande de paiement.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          9. Les recruteurs sur Deal.mg sont-ils des professionnels ou des
          particuliers ?
        </strong>
      ),
      answer: () => (
        <p>
          Les deux. Deal.mg est une plateforme ouverte aux entreprises
          (professionnels) qui publient des offres formelles, mais aussi aux
          particuliers cherchant à recruter pour des missions ponctuelles ou des
          services à domicile. Chaque annonceur est invité à vérifier son
          identité pour renforcer la confiance.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          10. Comment signaler un comportement suspect à Deal.mg ?
        </strong>
      ),
      answer: () => (
        <p>
          Vous pouvez signaler une annonce ou un utilisateur suspect via le
          bouton « Signaler » présent sur chaque annonce. L'équipe de modération
          traitera votre signalement pour protéger la communauté.
        </p>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Offres d'emploi à Madagascar | Recrutement sur Deal.mg</title>
        <meta
          name="description"
          content="Trouvez des offres d'emploi à Madagascar sur Deal.mg. CDI, CDD, stages et freelance. Plateforme fiable reliant recruteurs et candidats."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            name: "Offres d'emploi à Madagascar",
            image: ["https://deal.mg/logo.png"],
            description:
              "Découvrez les offres d'emploi et opportunités professionnelles à Madagascar sur Deal.mg",
            sku: idCategorie,
            offers: {
              "@type": "Offer",
              url: `https://deal.mg/article/resultat?idCategorie=${idCategorie}`,
              availability: "https://schema.org/InStock",
            },
          })}
        </script>
      </Helmet>

      <h1 className="text-center">
        Offres d'emploi à Madagascar : trouvez votre futur job sur Deal.mg
      </h1>

      <p>
        Vous êtes à la recherche d'une nouvelle opportunité professionnelle à
        Madagascar ? Deal.mg est la plateforme de référence pour découvrir des
        {" "}<strong>offres d'emploi en ligne</strong> dans tout le pays. Que vous
        soyez à Antananarivo, Toamasina ou Antsirabe, notre catégorie RH recense
        des milliers d'annonces pour tous les profils.
      </p>

      <p>
        <strong>Trouver un travail</strong> n'a jamais été aussi simple.
        Utilisez nos filtres de recherche pour cibler les secteurs qui
        recrutent. Sur Deal.mg, nous mettons en relation directe les recruteurs
        locaux et les candidats motivés.
      </p>
      <p>
        Contrairement aux groupes informels, Deal.mg vérifie les annonces pour
        vous éviter les arnaques à la <strong>recherche d'emploi</strong>.
        Postulez en toute confiance et trouvez le poste qui correspond à vos
        compétences.
      </p>
      <h2>Recrutement à Antananarivo et en province</h2>
      <p>
        Trouvez un emploi urgent à Antananarivo ou dans les principales villes
        de Madagascar. Deal.mg vous propose une sélection d'annonces
        géolocalisées pour trouver un poste près de chez vous. Que vous
        cherchiez un CDI, un CDD ou un job saisonnier, notre plateforme est
        votre partenaire emploi. Pour les entreprises, c'est l'endroit idéal
        pour poster une annonce de travail claire et atteindre des candidats
        qualifiés rapidement. Consultez les dernières offres et boostez votre
        carrière dès aujourd'hui sur la meilleure plateforme de recrutement à
        Madagascar. Il est également possible d'équiper votre bureau en matériel
        informatique via la{" "}
        <Link to="/electronique/tous?categorie=6">
          <strong>catégorie Électronique</strong>
        </Link>{" "}
        ou de trouver des locaux professionnels dans la section{" "}
        <Link to="/immobilier/tous?categorie=1">
          <strong>Immobilier</strong>
        </Link>{" "}
        pour développer votre activité. .
      </p>

      <br />

      <h2 className="support_faq_title">
        <MessageSquare size={20} className="support_faq_title_icon" />
        FAQ
      </h2>

      {faqList.map((item, index) => (
        <div
          key={index}
          className={`support_faq_item ${openIndex === index ? "open" : ""}`}
        >
          <div
            className="support_faq_question"
            onClick={() => toggleFAQ(index)}
          >
            <span>{item.question()}</span>
            <ChevronDown
              className={`support_faq_arrow ${
                openIndex === index ? "support_faq_rotate_180" : ""
              }`}
              size={20}
            />
          </div>

          <div
            className={`support_faq_answer_container${
              openIndex === index ? "active" : ""
            }`}
          >
            <div className="support_faq_answer">{item.answer()}</div>
          </div>
        </div>
      ))}
    </>
  );
};

export default FaqEmploi;
